import { cn } from '@/lib/cn';
import { es } from '@/i18n/es';
import { useUiStore } from '@/store/uiStore';
import { persistPreference } from '@/store/sessionStore';
import type { ContentType } from '@/types';
import { TypeIcon } from '@/components/ui/TypeBadge';

const OPTIONS: ContentType[] = ['fanfic', 'original'];

interface ContentTypeToggleProps {
  className?: string;
  /** 'compact' para el header en mobile. */
  size?: 'compact' | 'full';
}

/**
 * Toggle global Fanfic/Original. Escribe la preferencia en el perfil si hay
 * sesión, y siempre en localStorage vía el store persistido. Elegir un tipo
 * a mano limpia el override impuesto por una historia abierta.
 */
export function ContentTypeToggle({ className, size = 'full' }: ContentTypeToggleProps) {
  const contentType = useUiStore((s) => s.contentType);
  const setContentType = useUiStore((s) => s.setContentType);

  function select(type: ContentType) {
    if (type === contentType) return;
    setContentType(type);
    void persistPreference({ lastContentType: type });
  }

  return (
    <div
      role="radiogroup"
      aria-label={es.contentType.toggleLabel}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-pill border border-line bg-surface p-0.5',
        className,
      )}
    >
      {OPTIONS.map((type) => {
        const selected = type === contentType;
        const label = type === 'fanfic' ? es.contentType.fanfic : es.contentType.original;
        return (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => select(type)}
            data-content-type={type}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill font-medium transition-colors',
              size === 'compact' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
              selected
                ? 'bg-accent text-accent-ink'
                : 'text-muted hover:bg-surface-alt hover:text-ink',
            )}
          >
            <TypeIcon type={type} className="h-3.5 w-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
